import { createReducer, isAnyOf } from '@reduxjs/toolkit'
import dot from 'dot-prop-immutable'

import { IActionWithPayload } from 'common/core/interfaces/actions'
import {
  applySetInboxUser,
  fetchUsers,
  generateInviteLink,
  resetCurrentInboxUser,
  resetInviteLink,
  setAddPaidUserModalVisibility,
  setAutoAssignmentForUsers,
  setBillingAccess,
  setConversationsLimits,
  setCurrentInboxUser,
  setDeletePaidUserModalVisibility,
  setUserRole,
  updateBillingAccess,
  updateRole,
} from 'common/userRoles/actions/userRolesActions'
import * as atypes from 'common/userRoles/constants/UserRolesReduxActionTypes'

import { UsersState } from '../userRolesInterfaces'

const InitialState: UsersState = {
  inviteLink: null,
  items: null,
  isFetching: false,
  currentInboxUser: {
    userId: null,
    userName: '',
    isFetchingInboxUserStatus: false,
    isFetchingCheckboxStatus: false,
    isAddPaidUserModalVisible: false,
    isDeletePaidUserModalVisible: false,
  },
}

const getMemberIndex = (state: UsersState, userId: number) =>
  state.items?.findIndex((member) => member.user_id === userId)

export default createReducer(InitialState, (builder) => {
  builder
    .addCase(fetchUsers.pending, (state) => {
      state.isFetching = true
    })
    .addCase(fetchUsers.fulfilled, (state, action) => {
      state.isFetching = false
      state.items = action.payload || null
    })
    .addCase(fetchUsers.rejected, (state) => {
      state.isFetching = false
    })

    .addCase(setConversationsLimits.pending, (state, action) => {
      const { userIds } = action.meta.arg

      state.items = (state.items || []).map((item) => {
        if (userIds.includes(item.user_id)) {
          return { ...item, isThreadsLimitChanging: true }
        }
        return item
      })
    })
    .addCase(setConversationsLimits.fulfilled, (state, action) => {
      const { userIds, limit } = action.meta.arg

      state.items = (state.items || []).map((item) => {
        if (userIds.includes(item.user_id)) {
          return { ...item, threads_limit: limit, isThreadsLimitChanging: false }
        }
        return item
      })
    })
    .addCase(setConversationsLimits.rejected, (state, action) => {
      const { userIds } = action.meta.arg

      state.items = (state.items || []).map((item) =>
        userIds.includes(item.user_id) ? { ...item, isThreadsLimitChanging: false } : item,
      )
    })
    .addCase(setAutoAssignmentForUsers.fulfilled, (state, action) => {
      const { userIds, isEnabled } = action.meta.arg

      state.items = (state.items || []).map((item) =>
        userIds.includes(item.user_id) ? { ...item, lc_auto_assign_enabled: isEnabled } : item,
      )
    })
    .addCase(applySetInboxUser.pending, (state) => {
      state.currentInboxUser = {
        ...state.currentInboxUser,
        isFetchingInboxUserStatus: true,
        isFetchingCheckboxStatus: true,
      }
    })
    .addCase(applySetInboxUser.fulfilled, (state, action) => {
      const { userId, isInboxUser } = action.meta.arg
      const applied = action.payload?.applied

      if (!applied) return

      const memberIndex = getMemberIndex(state, userId)
      if (memberIndex !== -1 && state.items) {
        state.items = dot.set(state.items, `${memberIndex}.is_inbox_user`, isInboxUser)
      }
    })
    .addCase(setAddPaidUserModalVisibility, (state, action) => {
      state.currentInboxUser = {
        ...state.currentInboxUser,
        isFetchingCheckboxStatus: false,
        isAddPaidUserModalVisible: action.payload,
      }
    })
    .addCase(setDeletePaidUserModalVisibility, (state, action) => {
      state.currentInboxUser = {
        ...state.currentInboxUser,
        isFetchingCheckboxStatus: false,
        isDeletePaidUserModalVisible: action.payload,
      }
    })

    .addCase(resetInviteLink, (state) => {
      state.inviteLink = null
    })

    .addCase(generateInviteLink.pending, (state) => {
      state.inviteLink = null
    })
    .addCase(generateInviteLink.fulfilled, (state, action) => {
      state.inviteLink = action.payload || ''
    })

    .addCase(atypes.ACCESS_REQUESTS_APPROVE_RESPONSE, (state, action) => {
      const expectedAction = action as IActionWithPayload
      if (expectedAction.data.user) {
        state.items = [...(state.items || []), expectedAction.data.user]
      }
    })

    .addCase(setCurrentInboxUser, (state, { payload }) => {
      state.currentInboxUser = {
        ...state.currentInboxUser,
        userId: payload.userId,
        userName: payload.userName,
      }
    })
    .addCase(resetCurrentInboxUser, (state) => {
      state.currentInboxUser = {
        ...state.currentInboxUser,
        userId: null,
        userName: '',
      }
    })

    .addMatcher(isAnyOf(updateRole.fulfilled, setUserRole), (state, action) => {
      const { userId, role } = 'meta' in action ? action.meta.arg : action.payload
      const memberIndex = (state.items || []).findIndex((user) => user.user_id === userId)

      if (memberIndex !== -1 && state.items) {
        state.items = dot.set(state.items, `${memberIndex}.user_role`, role)
      }
    })
    .addMatcher(isAnyOf(updateBillingAccess.fulfilled, setBillingAccess), (state, action) => {
      const { userId, access } = 'meta' in action ? action.meta.arg : action.payload
      const memberIndex = (state.items || []).findIndex((user) => user.user_id === userId)

      if (memberIndex !== -1 && state.items) {
        state.items = dot.set(state.items, `${memberIndex}.has_billing_access`, access)
      }
    })
    .addMatcher(isAnyOf(applySetInboxUser.fulfilled, applySetInboxUser.rejected), (state) => {
      state.currentInboxUser = {
        ...state.currentInboxUser,
        isFetchingInboxUserStatus: false,
        isFetchingCheckboxStatus: false,
      }
    })
})
